import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { HelpCircle } from 'lucide-react';

/**
 * "Explain this change" popover for self-purchase / renewal audit events.
 * Reads `renewal_meta` written by `verify_user_payment` and renders the
 * three modes cleanly:
 *   - extension: prev_days_remaining + plan_days → new_expiry
 *   - fresh:     no prior subscription → +plan_days
 *   - upgrade:   old expiry dropped, fresh plan_days from today
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const Row = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-3 py-0.5">
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-900 text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const MODE_META = {
  extension: {
    title: 'Renewal · Extension',
    accent: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    footer: 'Days stack — remaining days are preserved on top of the new plan.',
  },
  fresh: {
    title: 'Fresh purchase',
    accent: 'bg-blue-50 border-blue-200 text-blue-800',
    footer: 'No active subscription — new expiry starts today.',
  },
  upgrade: {
    title: 'Upgrade · Fresh expiry',
    accent: 'bg-amber-50 border-amber-200 text-amber-800',
    footer: 'Upgrade drops the old expiry and applies the new plan fresh from today.',
  },
};

const ExplainRenewalButton = ({ meta }) => {
  if (!meta || !meta.mode || meta.added_days == null) return null;

  const {
    mode, plan_name, plan_price, plan_days,
    prev_expires_at, prev_days_remaining,
    added_days, new_expires_at,
  } = meta;
  const modeMeta = MODE_META[mode] || MODE_META.fresh;
  const totalDays = mode === 'extension'
    ? (Number(prev_days_remaining) + Number(added_days))
    : Number(added_days);

  return (
    <Popover>
      <PopoverTrigger
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline rounded"
        data-testid="explain-renewal-trigger"
      >
        <HelpCircle className="w-3 h-3" />
        Explain this change
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 text-xs"
        onClick={(e) => e.stopPropagation()}
        data-testid="explain-renewal-content"
      >
        <div className={`font-medium text-sm mb-2 px-2 py-1 rounded border ${modeMeta.accent}`}>
          {modeMeta.title}
        </div>

        <div className="space-y-2">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
            <div className="font-medium text-slate-700 mb-1">Plan purchased</div>
            <Row label="Plan" value={plan_name || '—'} />
            <Row label="Price" value={`₹${plan_price} / ${plan_days}d`} mono />
          </div>

          {mode === 'extension' && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="font-medium text-slate-700 mb-1">Previous subscription</div>
              <Row label="Prev expiry" value={fmtDate(prev_expires_at)} />
              <Row label="Days remaining" value={Number(prev_days_remaining).toFixed(2)} mono />
            </div>
          )}

          <div className="rounded-md bg-blue-50 border border-blue-200 p-2 space-y-1">
            <div className="font-medium text-blue-800">Formula</div>
            <div className="font-mono text-[11px] text-blue-900 break-all">
              {mode === 'extension' && (
                <>{Number(prev_days_remaining).toFixed(2)} + {added_days} = <strong>{Number(totalDays).toFixed(2)} days</strong></>
              )}
              {mode === 'fresh' && (
                <>0 + {added_days} = <strong>{added_days} days</strong></>
              )}
              {mode === 'upgrade' && (
                <>Reset → <strong>{added_days} days</strong> from today</>
              )}
            </div>
            <div className="text-[11px] text-blue-900">
              New expiry: <strong>{fmtDate(new_expires_at)}</strong>
            </div>
          </div>

          <div className="text-[10px] text-slate-400 italic pt-1 border-t">
            {modeMeta.footer}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ExplainRenewalButton;
